import React, { memo, useState } from 'react';
import { ListItemIcon, ListItemText, MenuItem, Select, SelectProps } from '@mui/material';
import TickIcon from '../../../assets/svg-fc/TickIcon';
import DropdownArrowUpIcon from '../../../assets/svg-fc/DropdownArrowUpIcon';
import DropdownArrowDownIcon from '../../../assets/svg-fc/DropdownArrowDownIcon';
import palette from '../../../theme/palette';
import typography from '../../../theme/typography';
import { IDropdownOption } from '../../../types/commonTypes';
import './dropdownV2.scss';

export interface iDropdownProps {
  disabled?: boolean;
  value: string;
  options: IDropdownOption[];
  onChange: (value: string) => void;
  renderValue?: ((value: string) => React.ReactNode | string) | undefined;
  labelStyle?: React.CSSProperties;
  menuStyle?: React.CSSProperties;
  menuHorizontalPosition?: 'left' | 'center' | 'right' | number;
  selectProps?: SelectProps<any>;
  onOpen?: () => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  className?: string;
  placeholder?: string;
  dropdownIconColor?: string;
}

const DropdownV2 = ({
  disabled,
  value,
  options,
  onChange,
  renderValue,
  labelStyle,
  menuStyle,
  menuHorizontalPosition = 'center',
  selectProps,
  onOpen,
  onBlur,
  className,
  dropdownIconColor,
}: iDropdownProps) => {
  const [open, setOpen] = useState<boolean>(false);
  return (
    <Select
      sx={{
        backgroundColor: 'transparent',
        '& .MuiSelect-select': {
          fontFamily: typography.fontPrimaryGeneralSans,
          fontWeight: typography.fontWeightMedium,
          color: palette.secondary.main,
          fontSize: '16px',
          padding: 0,
          ...labelStyle,
        },
        padding: 0,
      }}
      MenuProps={{
    slotProps: {
      paper: {
      style: menuStyle,
      },
        },
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: menuHorizontalPosition,
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: menuHorizontalPosition,
        },
      }}
      disabled={disabled}
      variant="standard"
      className={className}
      labelId="demo-simple-select-standard-label"
      id="demo-simple-select-standard"
      disableUnderline
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onOpen={() => {
        onOpen?.();
        setTimeout(() => setOpen(true), onOpen ? 500 : 0);
      }}
      onBlur={onBlur}
      onClose={() => setOpen(false)}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(!open);
      }}
      open={open}
      renderValue={renderValue || (() => options?.find((option) => option.id === value)?.value || '')}
      IconComponent={(e) =>
        open ? (
          <DropdownArrowUpIcon color={dropdownIconColor} className={`customDropDownIcon ${e.className}`} />
        ) : (
          <DropdownArrowDownIcon color={dropdownIconColor} className={`customDropDownIcon ${e.className}`} />
        )
      }
      {...selectProps}
    >
      {options?.map((option) => {
        const isSelected = option.id === value;
        return (
          <MenuItem
            sx={{
              fontFamily: typography.fontPrimaryGeneralSans,
              fontWeight: typography.fontWeightNormal,
              color: palette.secondary.main,
              fontSize: '16px',
              padding: '10px 20px',
              gap: '32px',
              '&.Mui-selected': {
                backgroundColor: '#F6F6F6',
              },
            }}
            key={option.id}
            disabled={option.disabled}
            value={option.id}
          >
            <ListItemText
              className="dropdownListItemText"
              primary={option.value}
              sx={{
                fontFamily: typography.fontFamily,
                fontWeight: typography.fontWeightNormal,
              }}
            />
            {isSelected && (
              <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                <TickIcon />
              </ListItemIcon>
            )}
          </MenuItem>
        );
      })}
    </Select>
  );
};

export default memo(DropdownV2);
